/**
 * Shared SSO utilities used across the customer-portal (Google) and
 * client-portal (Xero) SSO flows.
 *
 * Pure helpers (no I/O, no window access, safe to test in any environment):
 *   `buildSsoInitiateQueries`, `validateReturnPath`, `classifySsoCallback`,
 *   `classifyActivationFailure`, `buildRecoveryUrl`.
 *
 * I/O boundary functions (callers own redirect, side-effects, and error presentation):
 *   `initiateSso` -- calls the auth-proxy network and returns a redirect URL.
 *   `exchangeSsoCodeAndStoreTokens` -- calls the auth-proxy network and writes
 *   tokens to local storage.
 */

import { authProxyApi } from '@verifime/auth-proxy-definition';
import { storeTokens, TokensFromStorage, TokensToStore } from 'amazon-cognito-passwordless-auth/storage';
import { parseJwtPayload } from 'amazon-cognito-passwordless-auth/util';
import { CognitoIdTokenPayload } from 'amazon-cognito-passwordless-auth/jwt-model';
import type { RecoveryReason } from './model';

export type { RecoveryReason } from './model';

export type SsoProvider = 'google' | 'xero';

export type SsoPortal = 'customer' | 'client' | 'admin';

export type SsoInitiateQueries = {
  provider: SsoProvider;
  callbackUrl: string;
  returnPath: string;
  portal: SsoPortal | undefined;
};

export type SsoCallbackResult =
  | { status: 'success'; code: string; nonce: string; returnPath: string | null }
  | { status: 'cancelled' }
  | { status: 'failed' };

/**
 * Builds the query-string object that is forwarded to the SSO initiation
 * endpoint. The callback URL is derived from the application origin so the
 * provider can redirect back to the correct environment.
 */
export function buildSsoInitiateQueries(args: {
  provider: SsoProvider;
  origin: string;
  returnPath: string;
  portalEnv: SsoPortal | undefined;
}): SsoInitiateQueries {
  return {
    provider: args.provider,
    callbackUrl: `${args.origin}/auth/callback`,
    returnPath: args.returnPath,
    portal: args.portalEnv,
  };
}

/**
 * Client-side defence-in-depth check that a return path is a safe relative URL.
 * Rejects anything falsy, anything that does not start with '/', and paths that
 * begin with '//' or '/\' (protocol-relative or backslash tricks). Falls back to
 * the provided fallback (default '/'). This is not the primary open-redirect
 * guard: the auth proxy validates the callback URL server-side; this function
 * is an additional belt on the client.
 */
export function validateReturnPath(
  raw: string | null | undefined,
  fallback: string = '/',
): string {
  if (!raw) return fallback;
  if (!raw.startsWith('/')) return fallback;
  if (raw.startsWith('//')) return fallback;
  if (raw.startsWith('/\\')) return fallback;
  return raw;
}

/**
 * Inspects the URL parameters returned by the SSO provider after the redirect
 * and classifies the outcome. An 'error' param takes precedence: 'access_denied'
 * maps to cancelled (user explicitly declined), any other error maps to failed.
 * When no error is present, both 'code' and 'nonce' must exist for a successful
 * result; otherwise the callback is treated as failed.
 */
export function classifySsoCallback(params: URLSearchParams): SsoCallbackResult {
  const error = params.get('error');
  if (error !== null) {
    if (error === 'access_denied') return { status: 'cancelled' };
    return { status: 'failed' };
  }

  const code = params.get('code');
  const nonce = params.get('nonce');
  if (!code || !nonce) return { status: 'failed' };

  return {
    status: 'success',
    code,
    nonce,
    returnPath: params.get('returnPath'),
  };
}

/**
 * Extracts an HTTP status from the two error shapes the API client can throw:
 *   - 4xx: a raw AxiosError carrying `response.status`.
 *   - 5xx: the interceptor-rewritten shape `{ code, originalResponse }`, where
 *     `code` is the numeric status (see api-definition interceptorWithToken.ts).
 * Returns undefined for network errors and unrecognised shapes. Structural
 * checks keep this helper free of an axios dependency.
 */
function extractActivationStatus(error: unknown): number | undefined {
  const e = error as {
    response?: { status?: unknown };
    originalResponse?: { status?: unknown };
    code?: unknown;
  };
  if (typeof e?.response?.status === 'number') return e.response.status;
  if (typeof e?.originalResponse?.status === 'number') return e.originalResponse.status;
  if (typeof e?.code === 'number') return e.code;
  return undefined;
}

/**
 * Extracts the `error` string from the response body.
 * Handles both the raw Axios shape (`response.data.error`) and the
 * interceptor-rewritten shape (`originalResponse.data.error`).
 */
function extractErrorBodyCode(error: unknown): string | undefined {
  const e = error as {
    response?: { data?: { error?: unknown } };
    originalResponse?: { data?: { error?: unknown } };
  };
  const raw = e?.response?.data?.error ?? e?.originalResponse?.data?.error;
  return typeof raw === 'string' ? raw : undefined;
}

/**
 * Returns true when the activation failure signals that the customer must
 * step up with their existing credential (passkey or TOTP) before Google
 * SSO can be accepted. The signal is a 401 with the literal token
 * `STEP_UP_REQUIRED` in the response body's `error` field.
 */
export function isStepUpRequired(error: unknown): boolean {
  return (
    extractActivationStatus(error) === 401 &&
    (extractErrorBodyCode(error)?.includes('STEP_UP_REQUIRED') ?? false)
  );
}

/**
 * Classifies a failed tracking-reference activation into a recovery reason.
 * Default-deny: any non-2xx is a kick-out. 403 is the specific wrong-account
 * case, 5xx is our fault, 400/404 indicate an unrecognised reference (terminal),
 * and everything else (401/network/unknown) falls to the soft `failed` tier.
 */
export function classifyActivationFailure(
  error: unknown,
): Exclude<RecoveryReason, 'cancelled' | 'sign-in-required'> {
  const status = extractActivationStatus(error);
  if (status === 403) return 'wrong-account';
  if (status !== undefined && status >= 500) return 'server-error';
  if (status === 400 || status === 404) return 'broken-link';
  return 'failed';
}

/**
 * Builds the recovery redirect for a failed activation or a cancelled/failed SSO
 * callback. The tracking reference is the load-bearing binding and travels in the
 * path. The invited email is PII and is deliberately never added as a query
 * param (OWASP: information exposure via query strings); the user re-enters it on
 * the recovery page.
 *
 * `reason` is optional: when absent, no `?reason=` query string is appended and
 * the recovery page falls back to its default copy.
 */
export function buildRecoveryUrl(trackId: string, reason?: RecoveryReason): string {
  const path = `/recover-invitation/${encodeURIComponent(trackId)}`;
  return reason ? `${path}?reason=${reason}` : path;
}

/**
 * Thrown when token exchange succeeded but persisting the tokens failed.
 * Lets callers distinguish a storage failure from an exchange failure.
 */
export class SsoTokenStorageError extends Error {
  constructor(cause: unknown) {
    super('Failed to store SSO tokens');
    this.name = 'SsoTokenStorageError';
    this.cause = cause;
  }
}

/**
 * Calls the auth proxy to obtain a provider redirect URL for SSO initiation.
 * Caller performs the actual window.location redirect and owns loading/error
 * state and pre-redirect side-effects.
 */
export async function initiateSso(args: {
  provider: SsoProvider;
  origin: string;
  returnPath: string;
  portalEnv: SsoPortal | undefined;
}): Promise<string> {
  const queries = buildSsoInitiateQueries(args);
  const result = await authProxyApi.getAuthsso({ queries });
  if (!result.redirectUrl) {
    throw new Error('SSO service returned no redirect URL');
  }
  return result.redirectUrl;
}

/**
 * Exchanges the SSO authorisation code for tokens, persists them to local
 * storage, and returns the stored token set. Caller performs post-success
 * redirect and side-effects.
 * Throws SsoTokenStorageError when the exchange succeeded but storage failed,
 * letting callers distinguish a storage failure from a network failure.
 */
export async function exchangeSsoCodeAndStoreTokens(args: {
  code: string;
  nonce: string;
  portal: SsoPortal | undefined;
}): Promise<TokensFromStorage> {
  const { code, nonce, portal } = args;
  const result = await authProxyApi.postAuthssoexchange({ code, nonce, portal });
  const tokensToStore: TokensToStore = {
    accessToken: result.accessToken,
    idToken: result.idToken,
    refreshToken: result.refreshToken,
    expireAt: new Date(Date.now() + result.expiresIn * 1000),
  };
  try {
    await storeTokens(tokensToStore);
  } catch (storageErr) {
    throw new SsoTokenStorageError(storageErr);
  }
  const { 'cognito:username': username } = parseJwtPayload<CognitoIdTokenPayload>(result.idToken);
  return { ...tokensToStore, username };
}
